"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppButtonWithMenu = exports.AppButton = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_1 = require("react");
const constants_1 = require("../../../constants");
const utils_1 = require("../../../utils");
const Icon_1 = require("../../Icon");
const Link_1 = require("../../Link");
const Loader_1 = require("../../Loader");
const DropdownMenu_1 = require("../DropdownMenu");
const linearBoxShadow = 'lch(0 0 0 / 0.02) 0px 4px 4px -1px, lch(0 0 0 / 0.06) 0px 1px 1px 0px';
const STANDARD_DESIGN = {
    color: constants_1.Color.DARKER_GREY,
    backgroundColor: constants_1.Color.WHITE,
    borderColor: constants_1.Color.DARK_MEDIUM_GREY,
    iconColor: constants_1.Color.CHARCOAL,
    boxShadow: linearBoxShadow,
    divider: constants_1.Color.MEDIUM_GREY,
    hoverState: {
        filter: 'brightness(98%)',
    },
    disabledState: {
        color: constants_1.Color.DARK_MEDIUM_GREY,
        iconColor: constants_1.Color.DARK_MEDIUM_GREY,
        borderColor: constants_1.Color.MEDIUM_GREY,
        boxShadow: 'none',
    },
};
const BUTTON_COLORS_MAP = {
    standard: STANDARD_DESIGN,
    transparent: {
        ...STANDARD_DESIGN,
        borderColor: constants_1.Color.TRANSPARENT,
        backgroundColor: constants_1.Color.TRANSPARENT,
        boxShadow: 'none',
        divider: constants_1.Color.LIGHT_GREY,
        hoverState: {
            ...STANDARD_DESIGN.hoverState,
            backgroundColor: constants_1.Color.TRANSPARENT_GREY,
        },
    },
    error: {
        color: constants_1.Color.LIGHT_GREY,
        backgroundColor: constants_1.Color.RED,
        borderColor: constants_1.Color.RED,
        iconColor: constants_1.Color.WHITE,
        boxShadow: linearBoxShadow,
        divider: constants_1.Color.DARK_RED,
        hoverState: {
            filter: 'brightness(95%)',
        },
        disabledState: {
            color: constants_1.Color.MEDIUM_GREY,
            iconColor: constants_1.Color.MEDIUM_GREY,
            backgroundColor: constants_1.Color.MEDIUM_RED,
            borderColor: constants_1.Color.MEDIUM_RED,
            boxShadow: 'none',
            filter: 'transparentize(0.5)',
        },
    },
    warning: {
        ...STANDARD_DESIGN,
        hoverState: {
            color: constants_1.Color.RED,
            iconColor: constants_1.Color.MEDIUM_DARK_RED,
            filter: 'brightness(98%)',
            divider: constants_1.Color.DARK_RED,
        },
    },
    primary: {
        color: constants_1.Color.WHITE,
        backgroundColor: constants_1.Color.BLUE,
        borderColor: constants_1.Color.BLUE,
        iconColor: constants_1.Color.WHITE,
        divider: constants_1.Color.DARK_BLUE,
        boxShadow: linearBoxShadow,
        hoverState: {
            backgroundColor: constants_1.Color.DARK_BLUE,
        },
        disabledState: {
            color: constants_1.Color.DARK_GREY,
            iconColor: constants_1.Color.DARK_GREY,
            backgroundColor: constants_1.Color.MEDIUM_GREY,
            borderColor: constants_1.Color.DARK_MEDIUM_GREY,
            boxShadow: 'none',
        },
    },
    'primary-inverted': {
        ...STANDARD_DESIGN,
        color: constants_1.Color.BLUE,
        iconColor: constants_1.Color.DARK_BLUE,
        borderColor: constants_1.Color.BLUE,
        divider: constants_1.Color.DARK_BLUE,
        hoverState: {
            ...STANDARD_DESIGN.hoverState,
            color: constants_1.Color.DARK_BLUE,
            iconColor: constants_1.Color.DARKEST_BLUE,
            backgroundColor: constants_1.Color.LIGHTEST_BLUE,
        },
    },
    cta: {
        color: constants_1.Color.WHITE,
        backgroundColor: constants_1.Color.DARK_GREEN,
        borderColor: constants_1.Color.DARK_GREEN,
        iconColor: constants_1.Color.WHITE,
        divider: constants_1.Color.DARKER_GREEN,
        boxShadow: linearBoxShadow,
        hoverState: {
            filter: 'brightness(96%)',
        },
        disabledState: {
            color: constants_1.Color.DARK_GREY,
            iconColor: constants_1.Color.DARK_GREY,
            backgroundColor: constants_1.Color.MEDIUM_GREY,
            borderColor: constants_1.Color.DARK_MEDIUM_GREY,
            boxShadow: 'none',
        },
    },
    'cta-inverted': {
        ...STANDARD_DESIGN,
        color: constants_1.Color.DARK_GREEN,
        iconColor: constants_1.Color.DARK_GREEN,
        borderColor: constants_1.Color.DARK_GREEN,
        divider: constants_1.Color.DARK_GREEN,
        hoverState: {
            ...STANDARD_DESIGN.hoverState,
            color: constants_1.Color.DARKER_GREEN,
            iconColor: constants_1.Color.DARKER_GREEN,
            backgroundColor: constants_1.Color.LIGHTER_GREEN,
        },
    },
};
const BUTTON_SIZE_MAP = {
    xs: {
        paddingX: constants_1.theme.spacing[1],
        height: 24,
        borderRadius: constants_1.theme.border.radius.normal,
        fontSize: 12,
        iconSize: 'xs',
        gap: constants_1.theme.spacing[1],
    },
    sm: {
        paddingX: constants_1.theme.spacing[1.5],
        height: 28,
        borderRadius: constants_1.theme.border.radius.normal,
        fontSize: 12,
        iconSize: 'sm',
        gap: constants_1.theme.spacing[1],
    },
    md: {
        paddingX: constants_1.theme.spacing[2],
        height: 32,
        borderRadius: constants_1.theme.border.radius.normal,
        fontSize: 14,
        iconSize: 'sm',
        gap: constants_1.theme.spacing[1.5],
    },
    lg: {
        paddingX: constants_1.theme.spacing[2.5],
        height: 36,
        borderRadius: constants_1.theme.border.radius.normal,
        fontSize: 16,
        iconSize: 'md',
        gap: constants_1.theme.spacing[1.5],
    },
    xl: {
        paddingX: constants_1.theme.spacing[3],
        height: 40,
        borderRadius: constants_1.theme.border.radius.normal,
        fontSize: 18,
        iconSize: 'md',
        gap: constants_1.theme.spacing[2],
    },
};
const containerStyles = (props) => ({
    position: 'relative',
    // Animations
    transition: 'border, background-color, color, opacity 0.2s',
    // Borders
    borderWidth: 0.5,
    borderStyle: 'solid',
    // Sizing
    borderRadius: props.borderRadius,
    width: props.fullWidth ? '100%' : 'fit-content',
    height: props.height,
    // Text
    fontWeight: constants_1.theme.font.weight.medium,
    fontSize: props.fontSize,
    whiteSpace: 'nowrap',
    lineHeight: 'normal',
    // Colors
    boxShadow: props.boxShadow,
    backgroundColor: props.backgroundColor,
    borderColor: props.borderColor,
    color: props.color,
});
const StyledButton = styled_1.default.button((props) => {
    return {
        // Flexbox props was required for supporting Link buttons,
        // feels like it could be removed and fixed in some other way
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `0 ${props.paddingX}px`,
        svg: {
            color: props.iconColor,
        },
        ...(props.isContainer
            ? containerStyles(props)
            : {
                background: 'none',
                border: 'none',
                font: 'inherit',
                color: 'inherit',
                height: '100%',
                borderRight: props.rightDivider
                    ? `0.5px solid ${props.divider}`
                    : 'none',
            }),
        // Hover or popover is open
        "&[data-state='open'], &:hover": props.disabled
            ? undefined
            : {
                cursor: 'pointer',
                svg: {
                    color: props.hoverState?.iconColor,
                },
                ...props.hoverState,
            },
        // Disabled
        '&:disabled': {
            cursor: 'not-allowed',
            svg: {
                color: props.disabledState?.iconColor,
            },
            ...props.disabledState,
        },
        // Custom styles
        ...(0, utils_1.createStyles)(props.styles),
    };
});
const MenuButtonContainer = styled_1.default.div((props) => ({
    ...containerStyles({
        ...props,
        fullWidth: false,
        loading: false,
        isContainer: true,
    }),
    ...(props.disabled && { borderColor: props.disabledState.borderColor }),
    display: 'flex',
    overflow: 'hidden',
}));
const ContentWrapper = styled_1.default.div((props) => ({
    display: 'flex',
    gap: props.gap,
    alignItems: 'center',
    justifyContent: 'center',
    visibility: props.isLoading ? 'hidden' : 'visible',
}));
const LoaderWrapper = styled_1.default.div({
    left: '50%',
    pointerEvents: 'none',
    position: 'absolute',
    top: '50%',
    transform: 'scale(0.5) translate(-100%, -90%)',
    width: '100%',
    height: '100%',
});
const AppButton = (0, react_1.forwardRef)(({ children, ...props }, ref) => {
    const size = BUTTON_SIZE_MAP[props.size ?? 'sm'];
    const design = BUTTON_COLORS_MAP[props.design ?? 'standard'];
    const disabled = props.disabled; // || !!props.loading
    const linkProps = props.to && !disabled
        ? { to: props.to, openNewTab: props.openNewTab }
        : undefined;
    return ((0, jsx_runtime_1.jsxs)(StyledButton, { className: props.className, onClick: props.onClick, ref: ref, as: linkProps && !disabled ? Link_1.Link : undefined, disabled: disabled, fullWidth: props.fullWidth, loading: props.loading, isContainer: true, ...linkProps, ...size, ...design, children: [(0, jsx_runtime_1.jsxs)(ContentWrapper, { gap: size.gap, isLoading: props.loading, children: [props.icon && ((0, jsx_runtime_1.jsx)(Icon_1.Icon, { name: props.icon, size: size.iconSize, design: props.iconDesign, color: design.iconColor, flippedHorizontal: props.flipIconHorizontal })), (!!children || children === 0) && children] }), props.loading && ((0, jsx_runtime_1.jsx)(LoaderWrapper, { children: (0, jsx_runtime_1.jsx)(Loader_1.Loader, { color: design.iconColor }) }))] }));
});
exports.AppButton = AppButton;
AppButton.displayName = 'AppButton';
const AppButtonWithMenu = (0, react_1.forwardRef)(({ children, ...props }, ref) => {
    const size = BUTTON_SIZE_MAP[props.size ?? 'sm'];
    const design = BUTTON_COLORS_MAP[props.design ?? 'standard'];
    const disabled = props.disabled || !!props.loading;
    return ((0, jsx_runtime_1.jsxs)(MenuButtonContainer, { ...size, ...design, disabled: disabled, children: [(0, jsx_runtime_1.jsxs)(StyledButton, { onClick: props.onClick, ref: ref, disabled: disabled, fullWidth: props.fullWidth, loading: props.loading, isContainer: false, rightDivider: true, ...size, ...design, children: [(0, jsx_runtime_1.jsxs)(ContentWrapper, { gap: size.gap, isLoading: props.loading, children: [props.icon && ((0, jsx_runtime_1.jsx)(Icon_1.Icon, { name: props.icon, size: size.iconSize, design: props.iconDesign, color: design.iconColor, flippedHorizontal: props.flipIconHorizontal })), (!!children || children === 0) && children] }), props.loading && ((0, jsx_runtime_1.jsx)(LoaderWrapper, { children: (0, jsx_runtime_1.jsx)(Loader_1.Loader, { color: design.iconColor }) }))] }), (0, jsx_runtime_1.jsx)(DropdownMenu_1.DropdownMenu, { items: props.menu, disabled: disabled, trigger: (0, jsx_runtime_1.jsx)(StyledButton, { disabled: disabled || !props.menu.length, loading: false, fullWidth: false, isContainer: false, ...size, ...design, children: (0, jsx_runtime_1.jsx)(Icon_1.Icon, { name: "ChevronDownIcon", size: size.iconSize, design: props.iconDesign, color: design.iconColor }) }), side: "bottom", align: "end" })] }));
});
exports.AppButtonWithMenu = AppButtonWithMenu;
AppButtonWithMenu.displayName = 'AppButtonWithMenu';
